import type { ComponentDocMap } from './types'

/**
 * Prop documentation for the `chart` component.
 * Keep in sync with ChartProps in src/ui/uiConfig/components/Chart/Chart.types.ts
 * and ChartItem in the same file.
 */
export const chartPropDocs: ComponentDocMap = {
  items: {
    description:
      'Array of data series to display. Each item is connected to a fetched dataset via data_id === return_id.',
    type: 'ChartItem[]',
    example: '[{ title: "__Power output", data_id: "heat_unit_a", color: "$color-1", unit: "MW" }]',
  },
  'items[].display_title': {
    description:
      'Legend text shown exactly as given, without the pretty-name translation applied to title. For titles that are already translated.',
    type: 'string',
    example: '"KVV8: Min. el"',
  },
  'items[].y_axis_id': {
    description:
      'Assigns the series to a specific y-axis defined in config.options.scales. Use "y" (default left), "yCost", "yTemp", etc.',
    type: 'string',
    example: '"yCost"',
  },
  'items[].fill': {
    description:
      'Fills the area under the line. Can be boolean, a dataset index to fill toward, or a Chart.js fill target string.',
    type: 'boolean | number | string',
    example: 'true',
  },
  'items[].dashed': {
    description: 'Renders the series as a dashed line.',
    type: 'boolean',
    example: 'true',
  },
  'items[].opacity': {
    description: 'Applies semi-transparent line/fill styling for this series.',
    type: 'boolean',
    example: 'true',
  },
  'items[].config': {
    description: 'Per-series Chart.js dataset config merged into the generated dataset object.',
    type: 'Record<string, unknown>',
    example: '{ "stepped": true }',
  },
  'items[].order': {
    description:
      'Rendering/legend order (lower = drawn first, behind higher values). Also used by chart-linked export ordering in some views.',
    type: 'number',
    example: '-1',
  },
  'items[].default_hidden': {
    description: 'Hides the series in the legend and chart by default. Users can re-enable it.',
    type: 'boolean',
    example: 'true',
  },
  'items[].h_energy_bar': {
    description:
      'Marks this item for inclusion in horizontal energy bar charts when include_only_base_items_with_attr is used.',
    type: 'boolean',
    example: 'true',
  },
  'items[].tooltip_always_positive': {
    description:
      'Shows this series value as positive in tooltip formatting even when the underlying value is negative.',
    type: 'boolean',
    example: 'true',
  },
  'items[].tooltip_show_time_as_interval': {
    description: 'Shows tooltip timestamp as an interval (for example 09:00-10:00) instead of a single time.',
    type: 'boolean',
    example: 'true',
  },
  'items[].tooltip_do_not_show_value': {
    description: 'Hides this series value from tooltip rows while keeping the series visible in the chart.',
    type: 'boolean',
    example: 'true',
  },
  'items[].tooltip_do_not_show_zero_values': {
    description: 'Skips this series in the tooltip when the value is exactly zero.',
    type: 'boolean',
    example: 'true',
  },
  'items[].border_width': {
    description: 'Explicit line border width override for this series.',
    type: 'number',
    example: '1',
  },
  'items[].ignore_empty_filter': {
    description:
      'If true, keeps the dataset even when it is empty/all zeros after filtering. If false, empty datasets can be removed from rendering.',
    type: 'boolean',
    example: 'true',
  },
  'items[].show_as_negative': {
    description: 'Renders this series as negative values in the chart.',
    type: 'boolean',
    example: 'true',
  },
  'items[].show_only_in_export': {
    description: 'Includes this series in exports but hides it from the rendered chart.',
    type: 'boolean',
    example: 'true',
  },
  'items[].show_only_in_chart': {
    description: 'Shows this series in the chart UI only and excludes it from exports.',
    type: 'boolean',
    example: 'true',
  },
  'items[].do_not_show_item': {
    description: 'Hides this series from chart rendering while still allowing related data to be fetched.',
    type: 'boolean',
    example: 'true',
  },
  'items[].is_peak_unit': {
    description: 'Marks the series as a peak unit for views that distinguish peak production units.',
    type: 'boolean',
    example: 'false',
  },
  'items[].show_arrow_icon': {
    description: 'Shows up/down arrow indicator in tooltip based on the sign of this series value.',
    type: 'boolean',
    example: 'true',
  },
  config: {
    description:
      'Chart.js configuration object merged on top of the default config. Use for axis labels, scales, plugin options (zoom, legend, export button, etc.).',
    type: 'ChartConfigWithZoom',
    example:
      '{ options: { plugins: { chartLegend: { display: true, shared: false } }, scales: { yCost: { display: true, position: "right" } } } }',
  },
  'config.options.plugins.chartLegend.display': {
    description: 'Enables/disables shared chart legend rendering for linked charts.',
    type: 'boolean',
    example: 'true',
  },
  'config.options.plugins.chartLegend.shared': {
    description: 'If true, synchronizes legend and hover behavior between charts with the same groupId.',
    type: 'boolean',
    example: 'true',
  },
  'config.options.plugins.chartLegend.groupId': {
    description: 'Identifier used to connect multiple charts into one shared-legend interaction group.',
    type: 'string',
    example: '"productionPlan"',
  },
  'config.options.plugins.todayMarker.enabled': {
    description: 'Shows the vertical "now" marker line in the chart.',
    type: 'boolean',
    example: 'true',
  },
  'config.options.plugins.todayMarker.color': {
    description: 'Color for the today marker line.',
    type: 'string',
    example: '"#e0182d"',
  },
  'config.options.plugins.todayMarker.fillBeforeNow': {
    description: 'Adds a shaded background for the time range before the now marker.',
    type: 'boolean',
    example: 'true',
  },
  'config.options.scales.<axisId>.show_zero_line': {
    description:
      'Draws a thicker, darker line at the zero of this y-axis, on top of the chart contents so it stands out from the grid. Set it on the axis whose zero should be marked, e.g. config.options.scales.y. Disabled by default.',
    type: 'boolean',
    example: 'true',
  },
  'config.options.plugins.exportButton.display': {
    description: 'Shows or hides the chart export button.',
    type: 'boolean',
    example: 'false',
  },
  'config.options.plugins.verticalLine.enabled': {
    description: 'Enables vertical cursor/marker line plugin.',
    type: 'boolean',
    example: 'false',
  },
  'config.options.plugins.tooltip.position_offset_x': {
    description: 'Horizontal tooltip offset in pixels from cursor/anchor position.',
    type: 'number',
    example: '50',
  },
  'config.options.plugins.tooltip.position_offset_y': {
    description: 'Vertical tooltip offset in pixels from chart top.',
    type: 'number',
    example: '0',
  },
  filter_items: {
    description:
      'Groups items into user-selectable filter buttons shown above the chart. Each group has a title and a list of return_ids it controls.',
    type: 'FilterItems[]',
    example:
      '[{ title: "__Demands", return_ids: ["heatload_forecast", "heatload_outcome"] }, { title: "__Temperature", return_ids: ["temp_forecast"], default_hidden: true }]',
  },
  title: {
    description: 'Optional title displayed above the chart.',
    type: 'string',
    example: '"__Production plan"',
  },
  legendKey: {
    description:
      'Identity under which a chart built in code remembers its collapsed or open legend. Leave it unset in UI configs: a UI config chart is identified by its UI config id. The legend can only be collapsed on systems with the collapsible_legends feature flag.',
    type: 'string',
    example: '"appliedSettingValues"',
  },
  height: {
    description: 'Fixed pixel height for the chart canvas.',
    type: 'number',
    example: '300',
  },
  dataset_table: {
    description:
      'Renders a toggleable dataset table below the chart. Set { default: true } to show it by default, { display: true } to enable the toggle button.',
    type: '{ default: boolean; display: boolean }',
    example: '{ default: false, display: true }',
  },
  'dataset_table.enableCellRangeSelectionForClipboard': {
    description:
      'Explicitly enables rectangular cell range selection and clipboard copy in the chart-rendered dataset table. Defaults to false.',
    type: 'boolean',
    example: 'true',
  },
}
